import { z } from 'zod';

import { MEMBERSHIP_TYPES } from '../constants.js';

import { newPasswordSchema } from './auth.js';
import { isoDateTimeSchema, uuidSchema } from './common.js';

export const workspaceUserSchema = z.object({
  id: uuidSchema,
  userId: uuidSchema.nullable(),
  firstName: z.string().min(1).max(120),
  lastName: z.string().min(1).max(120),
  email: z.string().email().max(254).nullable(),
  title: z.string().max(160).nullable(),
  membershipType: z.enum(MEMBERSHIP_TYPES),
  companyId: uuidSchema.nullable(),
  isAdmin: z.boolean(),
  /** When true, company visibility follows access_matrix whitelist (employees/admins only). */
  accessRestricted: z.boolean(),
  avatarUrl: z.string().max(512).nullable(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type WorkspaceUserDto = z.infer<typeof workspaceUserSchema>;

/**
 * Admin-driven create:
 *   - if email+password are provided, an auth.User row is also created and linked
 *   - otherwise we get a workspace profile only (can be activated later)
 */
export const createWorkspaceUserSchema = z.object({
  firstName: z.string().min(1).max(120),
  lastName: z.string().min(1).max(120),
  email: z.string().email().max(254).nullable().optional(),
  title: z.string().max(160).nullable().optional(),
  membershipType: z.enum(MEMBERSHIP_TYPES).default('employee'),
  companyId: uuidSchema.nullable().optional(),
  isAdmin: z.boolean().optional(),
  /** Optional initial login password — required if you want this user to log in. */
  password: newPasswordSchema.optional(),
});
export type CreateWorkspaceUserDto = z.infer<typeof createWorkspaceUserSchema>;

export const updateWorkspaceUserSchema = z
  .object({
    firstName: z.string().min(1).max(120),
    lastName: z.string().min(1).max(120),
    email: z.string().email().max(254).nullable(),
    title: z.string().max(160).nullable(),
    membershipType: z.enum(MEMBERSHIP_TYPES),
    companyId: uuidSchema.nullable(),
    isAdmin: z.boolean(),
    accessRestricted: z.boolean(),
  })
  .partial();
export type UpdateWorkspaceUserDto = z.infer<typeof updateWorkspaceUserSchema>;

export const resetPasswordRequestSchema = z.object({
  password: newPasswordSchema,
});
export type ResetPasswordRequest = z.infer<typeof resetPasswordRequestSchema>;

/** Convenience: a display name composed from first/last (matches legacy `name` field). */
export function displayName(u: Pick<WorkspaceUserDto, 'firstName' | 'lastName'>): string {
  return `${u.firstName} ${u.lastName}`.trim();
}

/** Full name with optional job title, e.g. `Jane Doe · Designer`. */
export function displayNameWithTitle(
  u: Pick<WorkspaceUserDto, 'firstName' | 'lastName' | 'title'>,
): string {
  const name = displayName(u);
  const title = u.title?.trim();
  return title ? `${name} · ${title}` : name;
}

/** First + last initial, or a single letter when only one name part is present. */
export function displayNameInitials(u: Pick<WorkspaceUserDto, 'firstName' | 'lastName'>): string {
  const first = u.firstName.trim();
  const last = u.lastName.trim();
  if (!first && !last) return '—';
  if (!last) {
    const ch = first[0];
    return ch ? ch.toLocaleUpperCase('ru-RU') : '—';
  }
  const initials = (first[0] ?? '') + (last[0] ?? '');
  return initials ? initials.toLocaleUpperCase('ru-RU') : '—';
}
